import { useCallback, useContext, useSyncExternalStore } from 'react';
import type { ObjectData } from '@os/doc';
import { ExportModeContext } from '../editor/embeds';
import type { MediaResolver } from '../editor/lod';
import { coverCrop } from './image-fit';

const noSubscribe = () => () => undefined;

/**
 * Starts on the 256 px variant; the controller swaps `src` to the right LOD at rest, after decoding. Until a URL is
 * known (a peer's upload, the first `asset_urls` answer) there is no `src`, only the thumbhash placeholder.
 */
export function ImageObject({ o, media }: { o: Extract<ObjectData, { type: 'image' }>; media: MediaResolver }) {
  const subscribe = useCallback((cb: () => void) => media.subscribe?.(cb) ?? noSubscribe(), [media]);
  const snapshot = useSyncExternalStore(subscribe, () => `${media.urlFor(o.assetId, 256)}|${media.uploading?.(o.assetId) ? 1 : 0}`);
  const [url, uploading] = [snapshot.slice(0, snapshot.lastIndexOf('|')), snapshot.endsWith('|1')];
  const exporting = useContext(ExportModeContext);
  const placeholder = media.placeholder?.(o.assetId);
  const c = o.crop;
  const cropped = c.x !== 0 || c.y !== 0 || c.w !== 1 || c.h !== 1;
  return (
    <>
      <img
        data-asset={o.assetId}
        data-v="256"
        src={url || undefined}
        alt={o.alt}
        draggable={false}
        decoding="async"
        style={{
          // Cropped, the element is the whole source (its own ratio); otherwise it is the box, so the placeholder covers it.
          ...(placeholder ? { backgroundImage: `url("${placeholder}")`, backgroundSize: cropped ? '100% 100%' : 'cover', backgroundPosition: 'center' } : {}),
          // The whole source, scaled so that the crop covers the box undistorted (the object's element clips the rest).
          ...(cropped ? coverCrop(o, media.info?.(o.assetId) ?? null, o.crop) : {}),
        }}
        className={`pointer-events-none select-none ${cropped ? 'absolute max-w-none' : 'h-full w-full object-cover'}`}
      />
      {uploading && !exporting && <span data-uploading aria-hidden className="pointer-events-none absolute right-1 top-1 h-2.5 w-2.5 animate-pulse rounded-full bg-accent" />}
    </>
  );
}
